import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  DayPassSchema,
  HourBalanceSchema,
  MembershipActivateResponseSchema,
  MembershipListResponseSchema,
  MembershipSchema,
  MembershipTierListResponseSchema,
  PassPackSchema,
  StudioOccupancySchema,
  StudioSessionListResponseSchema,
  StudioSessionSchema,
  type ActivateMembershipInput,
  type BookStudioSessionInput,
  type CancelMembershipInput,
  type CreateMembershipInput,
  type DayPass,
  type Membership,
  type MembershipActivateResponse,
  type MembershipStatus,
  type PassPack,
  type RedeemPassPackInput,
  type SellDayPassInput,
  type StudioSession,
} from "@/api/types/membership";

/** Query-key factory — mirrors the bookings hook convention
 * (src/api/hooks/useBookings.ts). */
export const membershipKeys = {
  all: ["memberships"] as const,
  tiers: () => [...membershipKeys.all, "tiers"] as const,
  lists: () => [...membershipKeys.all, "list"] as const,
  list: (params: Record<string, unknown>) => [...membershipKeys.lists(), params] as const,
  details: () => [...membershipKeys.all, "detail"] as const,
  detail: (id: string | undefined) => [...membershipKeys.details(), id] as const,
  balance: (id: string | undefined, weekOf?: string) => [...membershipKeys.all, "balance", id, weekOf] as const,
  sessions: (id: string | undefined) => [...membershipKeys.all, "sessions", id] as const,
  occupancy: (resourceId: string | undefined, date: string) =>
    [...membershipKeys.all, "occupancy", resourceId, date] as const,
  dayPasses: (contactId?: string) => [...membershipKeys.all, "day-passes", contactId ?? "all"] as const,
  passPacks: (contactId?: string) => [...membershipKeys.all, "pass-packs", contactId ?? "all"] as const,
  studioSessions: (params: Record<string, unknown>) => [...membershipKeys.all, "studio-sessions", params] as const,
};

function invalidateAll(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: membershipKeys.all });
}

// ---- Tiers ------------------------------------------------------------------

export function useMembershipTiers(activeOnly = true) {
  return useQuery({
    queryKey: membershipKeys.tiers(),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/memberships/tiers?active_only=${activeOnly}`);
      return validateResponse(MembershipTierListResponseSchema, data, "GET /v2/memberships/tiers").items;
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ---- Memberships --------------------------------------------------------------

interface UseMembershipsParams {
  contact_id?: string;
  status?: MembershipStatus;
}

export function useMemberships(params: UseMembershipsParams = {}) {
  const { contact_id, status } = params;
  return useQuery({
    queryKey: membershipKeys.list({ contact_id, status }),
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (contact_id) searchParams.set("contact_id", contact_id);
      if (status) searchParams.set("status", status);
      const { data } = await apiClient.get(`/v2/memberships?${searchParams.toString()}`);
      return validateResponse(MembershipListResponseSchema, data, "GET /v2/memberships").items;
    },
    staleTime: 10 * 1000,
  });
}

export function useMembership(membershipId: string | undefined) {
  return useQuery<Membership>({
    queryKey: membershipKeys.detail(membershipId),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/memberships/${membershipId}`);
      return validateResponse(MembershipSchema, data, "GET /v2/memberships/:id");
    },
    enabled: !!membershipId,
  });
}

export function useCreateMembership() {
  const queryClient = useQueryClient();
  return useMutation<Membership, unknown, CreateMembershipInput>({
    mutationFn: async (input) => {
      const { data } = await apiClient.post("/v2/memberships", input);
      return validateResponse(MembershipSchema, data, "POST /v2/memberships");
    },
    onSuccess: (membership) => {
      queryClient.setQueryData(membershipKeys.detail(membership.id), membership);
      invalidateAll(queryClient);
    },
  });
}

export function useActivateMembership() {
  const queryClient = useQueryClient();
  return useMutation<MembershipActivateResponse, unknown, ActivateMembershipInput>({
    mutationFn: async ({ id, started_on }) => {
      const { data } = await apiClient.post(`/v2/memberships/${id}/activate`, { started_on });
      return validateResponse(MembershipActivateResponseSchema, data, "POST /v2/memberships/:id/activate");
    },
    onSuccess: (result) => {
      queryClient.setQueryData(membershipKeys.detail(result.membership.id), result.membership);
      invalidateAll(queryClient);
    },
  });
}

function useMembershipAction(endpoint: string, label: string) {
  const queryClient = useQueryClient();
  return useMutation<Membership, unknown, string>({
    mutationFn: async (id) => {
      const { data } = await apiClient.post(`/v2/memberships/${id}/${endpoint}`, {});
      return validateResponse(MembershipSchema, data, `POST /v2/memberships/:id/${label}`);
    },
    onSuccess: (membership) => {
      queryClient.setQueryData(membershipKeys.detail(membership.id), membership);
      invalidateAll(queryClient);
    },
  });
}

export function usePauseMembership() {
  return useMembershipAction("pause", "pause");
}

export function useResumeMembership() {
  return useMembershipAction("resume", "resume");
}

export function useCancelMembership() {
  const queryClient = useQueryClient();
  return useMutation<Membership, unknown, CancelMembershipInput>({
    mutationFn: async ({ id, effective_date, admin_override }) => {
      const { data } = await apiClient.post(`/v2/memberships/${id}/cancel`, {
        effective_date: effective_date ?? null,
        admin_override: admin_override ?? false,
      });
      return validateResponse(MembershipSchema, data, "POST /v2/memberships/:id/cancel");
    },
    onSuccess: (membership) => {
      queryClient.setQueryData(membershipKeys.detail(membership.id), membership);
      invalidateAll(queryClient);
    },
  });
}

export function useCreateMembershipPortalLink() {
  return useMutation<string, unknown, string>({
    mutationFn: async (membershipId) => {
      const { data } = await apiClient.post(`/v2/memberships/${membershipId}/portal-link`, {});
      return (data as { portal_url: string }).portal_url;
    },
  });
}

// ---- Hour balance -------------------------------------------------------------

export function useMembershipBalance(membershipId: string | undefined, weekOf?: string) {
  return useQuery({
    queryKey: membershipKeys.balance(membershipId, weekOf),
    queryFn: async () => {
      const qs = weekOf ? `?week_of=${weekOf}` : "";
      const { data } = await apiClient.get(`/v2/memberships/${membershipId}/balance${qs}`);
      return validateResponse(HourBalanceSchema, data, "GET /v2/memberships/:id/balance");
    },
    enabled: !!membershipId,
    staleTime: 10 * 1000,
  });
}

export function useMembershipSessions(membershipId: string | undefined) {
  return useQuery({
    queryKey: membershipKeys.sessions(membershipId),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/memberships/${membershipId}/sessions`);
      return validateResponse(StudioSessionListResponseSchema, data, "GET /v2/memberships/:id/sessions").items;
    },
    enabled: !!membershipId,
    staleTime: 10 * 1000,
  });
}

// ---- Studio occupancy + sessions ------------------------------------------------

export function useStudioOccupancy(resourceId: string | undefined, sessionDate: string) {
  return useQuery({
    queryKey: membershipKeys.occupancy(resourceId, sessionDate),
    queryFn: async () => {
      const searchParams = new URLSearchParams({ session_date: sessionDate });
      if (resourceId) searchParams.set("resource_id", resourceId);
      const { data } = await apiClient.get(`/v2/memberships/studio/occupancy?${searchParams.toString()}`);
      return validateResponse(StudioOccupancySchema, data, "GET /v2/memberships/studio/occupancy");
    },
    enabled: !!sessionDate,
    staleTime: 10 * 1000,
  });
}

export function useStudioSessions(params: { contact_id?: string; membership_id?: string; date_from?: string; date_to?: string } = {}) {
  return useQuery({
    queryKey: membershipKeys.studioSessions(params),
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params.contact_id) searchParams.set("contact_id", params.contact_id);
      if (params.membership_id) searchParams.set("membership_id", params.membership_id);
      if (params.date_from) searchParams.set("date_from", params.date_from);
      if (params.date_to) searchParams.set("date_to", params.date_to);
      const { data } = await apiClient.get(`/v2/memberships/studio-sessions?${searchParams.toString()}`);
      return validateResponse(StudioSessionListResponseSchema, data, "GET /v2/memberships/studio-sessions").items;
    },
    staleTime: 10 * 1000,
  });
}

export function useBookStudioSession() {
  const queryClient = useQueryClient();
  return useMutation<StudioSession, unknown, BookStudioSessionInput>({
    mutationFn: async (input) => {
      const { data } = await apiClient.post("/v2/memberships/studio-sessions", input);
      return validateResponse(StudioSessionSchema, data, "POST /v2/memberships/studio-sessions");
    },
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useCancelStudioSession() {
  const queryClient = useQueryClient();
  return useMutation<StudioSession, unknown, string>({
    mutationFn: async (sessionId) => {
      const { data } = await apiClient.post(`/v2/memberships/studio-sessions/${sessionId}/cancel`, {});
      return validateResponse(StudioSessionSchema, data, "POST /v2/memberships/studio-sessions/:id/cancel");
    },
    onSuccess: () => invalidateAll(queryClient),
  });
}

// ---- Day passes / pass packs -------------------------------------------------

export function useDayPasses(contactId?: string) {
  return useQuery({
    queryKey: membershipKeys.dayPasses(contactId),
    queryFn: async () => {
      const qs = contactId ? `?contact_id=${contactId}` : "";
      const { data } = await apiClient.get(`/v2/memberships/day-passes${qs}`);
      return validateResponse(DayPassSchema.array(), data, "GET /v2/memberships/day-passes");
    },
    enabled: contactId !== undefined,
    staleTime: 10 * 1000,
  });
}

export function useSellDayPass() {
  const queryClient = useQueryClient();
  return useMutation<DayPass, unknown, SellDayPassInput>({
    mutationFn: async (input) => {
      const { data } = await apiClient.post("/v2/memberships/day-passes", input);
      return validateResponse(DayPassSchema, data, "POST /v2/memberships/day-passes");
    },
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function usePassPacks(contactId?: string) {
  return useQuery({
    queryKey: membershipKeys.passPacks(contactId),
    queryFn: async () => {
      const qs = contactId ? `?contact_id=${contactId}` : "";
      const { data } = await apiClient.get(`/v2/memberships/pass-packs${qs}`);
      return validateResponse(PassPackSchema.array(), data, "GET /v2/memberships/pass-packs");
    },
    enabled: contactId !== undefined,
    staleTime: 10 * 1000,
  });
}

export function useSellPassPack() {
  const queryClient = useQueryClient();
  return useMutation<PassPack, unknown, string>({
    mutationFn: async (contactId) => {
      const { data } = await apiClient.post("/v2/memberships/pass-packs", { contact_id: contactId });
      return validateResponse(PassPackSchema, data, "POST /v2/memberships/pass-packs");
    },
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useRedeemPassPack() {
  const queryClient = useQueryClient();
  return useMutation<DayPass, unknown, RedeemPassPackInput>({
    mutationFn: async ({ pack_id, pass_date }) => {
      const { data } = await apiClient.post(`/v2/memberships/pass-packs/${pack_id}/redeem`, { pass_date });
      return validateResponse(DayPassSchema, data, "POST /v2/memberships/pass-packs/:id/redeem");
    },
    onSuccess: () => invalidateAll(queryClient),
  });
}
